import { CheckOutlined, DeleteOutlined, EditOutlined, LoadingOutlined, StopOutlined } from '@ant-design/icons'
import { Button, Checkbox, DatePicker, Input, Tooltip } from 'antd'
import { chunk } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { SiteLink } from '@/components'
import { CareerProfileInput, ExperienceType, ValueVisibilityInput } from '@/generated/graphql'

import s from './profile.module.less'
import { getDateRange, setCareerPartialAttribute } from './profile.util'

export const createCareerItem = () => {
  return {
    organization: '',
    position: '',
    jobDescription: '',
    duration: `${moment().format('YYYY-MM-DD')}_${moment().format('YYYY-MM-DD')}`,
    experienceType: ExperienceType.Career,
  }
}

interface ExperienceItemProps {
  index: number
  isEdit: boolean
  root: CareerProfileInput[] | undefined
  career: CareerProfileInput
  deleteArrayItem: (array: CareerProfileInput[], index: number) => void
  setEditedCareerProfile: React.Dispatch<React.SetStateAction<CareerProfileInput[] | undefined>>
}
export const ExperienceItem: React.FC<ExperienceItemProps> = ({
  index,
  isEdit,
  root,
  career,
  deleteArrayItem,
  setEditedCareerProfile,
}) => {
  const { t } = useTranslation()

  const items: any[] = [
    {
      title: <span>{t('profile.workExperience.organization')}:</span>,
      value: { value: career.organization, isVisible: true },
      attributeChain: 'organization',
      disableHidden: true,
    },
    {
      title: <span>{t('profile.workExperience.position')}:</span>,
      value: { value: career.position, isVisible: true },
      attributeChain: 'position',
      disableHidden: true,
    },
    {
      title: <span>{t('profile.workExperience.duration')}:</span>,
      value: { value: career.duration, isVisible: true },
      attributeChain: 'duration',
      disableHidden: true,
      type: 'dateRangePicker',
    },
    {
      title: <span>{t('profile.workExperience.jobDescription')}:</span>,
      value: { value: career.jobDescription, isVisible: true },
      attributeChain: 'jobDescription',
      disableHidden: true,
      rows: 6,
    },
  ]

  const [left, right] = chunk(items, 3).map((item) =>
    item.map((intro, i) =>
      isEdit || intro.value.isVisible ? (
        <ProfileInfo
          title={intro.title}
          value={intro.value}
          editMode={isEdit}
          isCareer={true}
          key={i}
          type={intro.type || 'textarea'}
          rows={intro.rows}
          onChange={(value, chain) =>
            setCareerPartialAttribute<CareerProfileInput>(career, index, root!, chain, value, setEditedCareerProfile)
          }
          attributeChain={intro.attributeChain}
          link={intro.link}
          readOnly={!!intro.readOnly}
          disableHidden={!!intro.disableHidden}
        />
      ) : null
    )
  )
  return (
    <div className={s.ExperienceItem}>
      <div className={s.content}>
        <div className={s.left}>{left}</div>
        <div className={s.right}>{right}</div>
      </div>

      {isEdit && (
        <div className={s.buttonGroup}>
          <Button danger icon={<DeleteOutlined />} onClick={() => deleteArrayItem(root!, index)}>
            {t('profile.workExperience.removeWorkExperience')}
          </Button>
        </div>
      )}
    </div>
  )
}

interface ProfileInfoProps {
  title: string | React.ReactNode
  value: ValueVisibilityInput
  attributeChain: string
  editMode: boolean
  onChange: (value: any, chain: string) => void
  isCareer?: boolean
  link?: string
  readOnly: boolean
  disableHidden: boolean
  rows?: number
  type?: 'link' | 'textarea' | 'dateRangePicker'
}

export const ProfileInfo: React.FC<ProfileInfoProps> = ({
  title,
  value,
  editMode,
  attributeChain,
  type = 'textarea',
  readOnly,
  children,
  disableHidden,
  isCareer = false,
  rows = 1,
  onChange,
}) => {
  const { t } = useTranslation()
  const { value: displayValue } = value

  const header = (
    <div className={s.sectionItemHeader}>
      {title}
      {editMode && !disableHidden && (
        <Checkbox
          style={{ marginLeft: 4 }}
          checked={value.isVisible}
          onChange={(e) => onChange(e.target.checked, `${attributeChain}.isVisible`)}
        >
          {t('profile.show')}
        </Checkbox>
      )}
    </div>
  )

  const showContent = children ? (
    children
  ) : type === 'link' ? (
    <SiteLink href={displayValue}>{displayValue}</SiteLink>
  ) : (
    <span className={s.readArea}>{displayValue}</span>
  )

  const editContent = readOnly ? (
    showContent
  ) : children ? (
    children
  ) : type === 'dateRangePicker' ? (
    <DatePicker.RangePicker
      style={{ width: '100%' }}
      value={getDateRange(displayValue)}
      onChange={(_, formatStrings) => {
        onChange(
          formatStrings.map((date) => date.replace(/-/g, '.')).join(' - '),
          isCareer ? attributeChain : `${attributeChain}.value`
        )
      }}
    ></DatePicker.RangePicker>
  ) : (
    <Input.TextArea
      wrap='off'
      rows={rows}
      style={{ overflow: 'hidden' }}
      className={editMode && !readOnly ? s.writeArea : s.readArea}
      value={displayValue}
      readOnly={!editMode}
      onChange={(e) => onChange(e.target.value, isCareer ? attributeChain : `${attributeChain}.value`)}
      placeholder={t('profile.placeholder')}
    />
  )

  const content = editMode ? editContent : showContent

  return (
    <div className={s.sectionItem}>
      {header}
      {content}
    </div>
  )
}

interface EditButtonProps {
  isViewer: boolean
  isEdit: boolean
  loading: boolean
  noEditIcon?: boolean
  onEdit: (isEdit: boolean) => void
  onUpdate: () => Promise<void>
}

export const EditButton: React.FC<EditButtonProps> = ({
  isViewer,
  isEdit,
  loading,
  noEditIcon = false,
  onEdit,
  onUpdate,
}) => {
  const { t } = useTranslation()
  return isViewer ? (
    <>
      {isEdit ? (
        <>
          <Tooltip title={t('profile.cancelEdit')}>
            {loading ? (
              <LoadingOutlined />
            ) : (
              <StopOutlined
                style={{ color: '#bb4848' }}
                onClick={() => {
                  onEdit(false)
                }}
              />
            )}
          </Tooltip>
          <Tooltip title={t('profile.confirmEdit')}>
            {loading ? (
              <LoadingOutlined />
            ) : (
              <CheckOutlined
                disabled={loading}
                onClick={async () => {
                  await onUpdate()
                }}
              />
            )}
          </Tooltip>
        </>
      ) : !noEditIcon ? (
        loading ? (
          <LoadingOutlined />
        ) : (
          <Tooltip title={t('profile.edit')}>
            <EditOutlined
              disabled={loading}
              onClick={() => {
                onEdit(true)
              }}
            />
          </Tooltip>
        )
      ) : null}
    </>
  ) : null
}
